import React, { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import { Building2, Users, BarChart3, Shield } from 'lucide-react';

const schema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
      'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (@$!%*?&)'
    )
    .required('Password is required'),
  confirmPassword: yup.string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Please confirm your password'),
  clubName: yup.string().required('Club name is required'),
  address: yup.string().required('Address is required'),
});

type RegisterFormData = yup.InferType<typeof schema>;

export const ClubAdminRegisterPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string>('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: yupResolver(schema),
  });

  if (isAuthenticated) {
    return <Navigate to="/administration-club/dashboard" replace />;
  }

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setServerError('');
    try {
      navigate('/administration-club/select-tier', {
        state: {
          registrationData: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            password: data.password,
            clubName: data.clubName,
            address: data.address,
          }
        }
      });
    } catch (error: any) {
      setServerError(error.response?.data?.error?.message || 'Registration failed');
      setIsLoading(false);
    }
  };

  const inputClass = (hasError: boolean) =>
    `w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${hasError ? 'border-red-400' : 'border-gray-300'}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">

        {/* Left Side - Branding & Features */}
        <div className="hidden lg:block">
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              <span className="text-gray-900">Game</span>
              <span className="text-green-600">Path</span>
              <span className="text-lg font-normal text-gray-500 ml-2">for Clubs</span>
            </h1>
            <p className="text-xl text-gray-600">
              Manage multiple teams, coaches, and players across your entire club
            </p>
          </div>

          <div className="space-y-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Building2 className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Club-Wide Overview</h3>
                <p className="text-gray-600 text-sm">See all teams, coaches, and players from a single dashboard</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Multi-Team Management</h3>
                <p className="text-gray-600 text-sm">Organise squads by age group with dedicated coaches for each</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Cross-Team Reporting</h3>
                <p className="text-gray-600 text-sm">Compare development trends across age groups and teams</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Role-Based Access</h3>
                <p className="text-gray-600 text-sm">Admins oversee everything while coaches manage their own teams</p>
              </div>
            </div>
          </div>

          {/* Coach Signup Message */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-gray-700">
              Just managing one team? You can{' '}
              <Link to="/register" className="text-green-600 hover:text-green-700 font-medium underline">sign up as a coach instead</Link>
            </p>
          </div>
        </div>

        {/* Right Side - Registration Form */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 lg:p-10">
          {/* Mobile Header */}
          <div className="lg:hidden mb-6">
            <h1 className="text-3xl font-bold mb-1">
              <span className="text-gray-900">Game</span>
              <span className="text-green-600">Path</span>
            </h1>
            <p className="text-gray-600">Register your club</p>
          </div>

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Create Club Admin Account</h2>
            <p className="text-gray-600">
              Start your 14-day free trial. No credit card required until trial ends.
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Personal Information Section */}
            <div className="bg-gray-50 rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Personal Information</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                  <input id="firstName" type="text" {...register('firstName')} className={inputClass(!!errors.firstName)} />
                  {errors.firstName && <p className="mt-1 text-xs text-red-600">{errors.firstName.message}</p>}
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                  <input id="lastName" type="text" {...register('lastName')} className={inputClass(!!errors.lastName)} />
                  {errors.lastName && <p className="mt-1 text-xs text-red-600">{errors.lastName.message}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input id="email" type="email" {...register('email')} className={inputClass(!!errors.email)} />
                {errors.email && <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <input id="password" type="password" {...register('password')} className={inputClass(!!errors.password)} />
                <ul className="mt-1 text-xs text-gray-500 space-y-0.5 pl-4 list-disc">
                  <li>At least 8 characters</li>
                  <li>One uppercase letter (A-Z)</li>
                  <li>One lowercase letter (a-z)</li>
                  <li>One number (0-9)</li>
                  <li>One special character (@$!%*?&amp;)</li>
                </ul>
                {errors.password && <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <input id="confirmPassword" type="password" {...register('confirmPassword')} className={inputClass(!!errors.confirmPassword)} />
                {errors.confirmPassword && <p className="mt-1 text-xs text-red-600">{errors.confirmPassword.message}</p>}
              </div>
            </div>

            {/* Club Information Section */}
            <div className="bg-gray-50 rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-semibold text-gray-900">Club Information</h3>

              <div>
                <label htmlFor="clubName" className="block text-sm font-medium text-gray-700 mb-1">Club Name</label>
                <input id="clubName" type="text" {...register('clubName')} className={inputClass(!!errors.clubName)} />
                {errors.clubName && <p className="mt-1 text-xs text-red-600">{errors.clubName.message}</p>}
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">Registered Club Address</label>
                <input id="address" type="text" placeholder="Full address including postcode" {...register('address')} className={inputClass(!!errors.address)} />
                {errors.address && <p className="mt-1 text-xs text-red-600">{errors.address.message}</p>}
              </div>
            </div>

            {serverError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-600">{serverError}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Processing...' : 'Continue to Plan Selection'}
            </button>
          </form>

          <div className="text-center mt-5">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/administration-club" className="text-green-600 hover:text-green-700 font-medium">Sign in</Link>
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};
